// @ts-nocheck
/**
 * chartTypeMigration.ts — Migrate legacy chart payload fields (DEC-056).
 *
 * - bar/pie + seriesMode:'frequencies' → barFrequencies / pieFrequencies
 * - legacy yColumn/ySource/seriesColor/yMin/yMax → series[0]
 */

import type {
  ChartSeriesDef,
  LayoutChartType,
} from '../../types/component';
import type { ValueSource } from '../../types/binding';

const LAYOUT_CHART_TYPES = new Set<LayoutChartType>([
  'line',
  'scatter',
  'timeline',
  'bar',
  'barFrequencies',
  'pie',
  'pieFrequencies',
  'histogram',
  'box',
]);

export const MAX_CHART_SERIES = 8;

export const XY_MULTI_SERIES_CHART_TYPES = new Set<LayoutChartType>([
  'line',
  'scatter',
  'timeline',
]);

export function isLayoutChartTypeToken(raw: unknown): raw is LayoutChartType {
  return typeof raw === 'string' && LAYOUT_CHART_TYPES.has(raw as LayoutChartType);
}

/**
 * Given a resolved or static chartType string plus optional legacy seriesMode,
 * return the migrated LayoutChartType token.
 */
export function migrateLayoutChartType(
  chartType: LayoutChartType | string | undefined,
  seriesMode?: string | null,
): LayoutChartType {
  const base =
    typeof chartType === 'string' && isLayoutChartTypeToken(chartType)
      ? chartType
      : 'line';
  if (seriesMode === 'frequencies') {
    if (base === 'bar') return 'barFrequencies';
    if (base === 'pie') return 'pieFrequencies';
  }
  return base;
}

function migrateFrequenciesInPlace(chart: Record<string, unknown>): void {
  const seriesMode = chart.seriesMode;
  delete chart.seriesMode;

  if (seriesMode !== 'frequencies') return;

  const chartType = chart.chartType;
  if (typeof chartType === 'string') {
    chart.chartType = migrateLayoutChartType(chartType, 'frequencies');
    return;
  }
  if (
    chartType &&
    typeof chartType === 'object' &&
    (chartType as ValueSource<LayoutChartType>).kind === 'static'
  ) {
    const staticSrc = chartType as { kind: 'static'; value: unknown };
    if (typeof staticSrc.value === 'string') {
      chart.chartType = {
        kind: 'static',
        value: migrateLayoutChartType(staticSrc.value, 'frequencies'),
      };
    }
  }
}

/**
 * Mutate a chart payload in place:
 * 1) frequencies → chartType tokens
 * 2) legacy single Y fields → series[0]
 * Strips deprecated top-level yColumn/ySource/seriesColor/yMin/yMax after migrate.
 */
export function migrateChartPayloadInPlace(chart: Record<string, unknown>): void {
  migrateFrequenciesInPlace(chart);

  const existingSeries = chart.series;
  if (Array.isArray(existingSeries) && existingSeries.length > 0) {
    delete chart.yColumn;
    delete chart.ySource;
    delete chart.seriesColor;
    delete chart.yMin;
    delete chart.yMax;
    return;
  }

  const yColumn = typeof chart.yColumn === 'string' ? chart.yColumn : undefined;
  const ySource = chart.ySource;
  const hasLegacyY =
    (typeof yColumn === 'string' && yColumn.length > 0) ||
    ySource != null;

  const seriesEntry: ChartSeriesDef = {
    id: 'y',
  };
  if (typeof yColumn === 'string' && yColumn.length > 0) {
    seriesEntry.yColumn = yColumn;
  }
  if (ySource != null) {
    seriesEntry.ySource = ySource as ValueSource<unknown>;
  }
  if (chart.seriesColor != null) {
    seriesEntry.color = chart.seriesColor as ValueSource<string>;
  }
  if (chart.yMin != null) {
    seriesEntry.yMin = chart.yMin as ValueSource<number>;
  }
  if (chart.yMax != null) {
    seriesEntry.yMax = chart.yMax as ValueSource<number>;
  }

  if (hasLegacyY) {
    chart.series = [seriesEntry];
  } else if (!Array.isArray(existingSeries)) {
    chart.series = [
      {
        id: 'y',
      } satisfies ChartSeriesDef,
    ];
  }

  delete chart.yColumn;
  delete chart.ySource;
  delete chart.seriesColor;
  delete chart.yMin;
  delete chart.yMax;
}

export { LAYOUT_CHART_TYPES };
